import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  GridLegacy as Grid,
  InputAdornment,
  InputLabel,
  IconButton,
  Link,
  ListSubheader,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import viewIcon from '@old/assets/svg/core/eye-hollow-blue.svg';
import DeleteIcon from '@old/assets/svg/core/trash-red.svg';
import SearchIcon from '@old/assets/svg/search.svg';
import { useGlobalStore } from '@old/global-store/use-store';
import { useDebounce, useEffectOnUpdate, useToggle } from '@old/hooks/core';
import { useService } from '@old/hooks/use-service';
import { listCountrysideService } from '@old/services/domain/base/countryside/countryside-list';
import { listLotsService } from '@old/services/domain/base/lot/lot-list';
import { listSilobagService } from '@old/services/domain/base/silobag/base/silobag-list';
import { harvestListService } from '@old/services/domain/base/silobag/list/harvest-list';
import { productListService } from '@old/services/domain/base/silobag/list/product-list';
import { silobagTagsListService } from '@old/services/domain/base/silobag/list/silobag-tags-list';
import { speciesListService } from '@old/services/domain/base/silobag/list/species-list';
import { stylesInput } from '@old/styles/generic-styles';
import SingleInputDateRangePicker from '@old/ui/date-range/date-range';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import i18n from '../../../libs/language';
import { SilobagListTablet } from './silobag-tablet';
// import es from 'dayjs/locale/es';
import ExpandMore from '@old/assets/svg/expand-more-blue.svg';
import CloseIcon from '@old/assets/svg/core/cross.svg';

import { DYNAMIC_PATH } from '@old/data/core/paths';
import { Selector } from '@old/ui/selector/selector';
import { Dayjs } from 'dayjs';
import { useShallow } from 'zustand/react/shallow';

type AlertLevelProp = {
  id: number;
  name: string;
  color: 'error' | 'warning' | 'secondary' | 'primary';
};

const label = { inputProps: { 'aria-label': 'Checkbox demo' } };

type SummarySilobagList = Awaited<ReturnType<typeof listSilobagService>>['summary']['data'];
type queryParametersSbList = Parameters<typeof listSilobagService>[0]['query'];
type SummaryCountrysidesList = Awaited<ReturnType<typeof listCountrysideService>>['summary'];
type SummarySpeciesList = Awaited<ReturnType<typeof speciesListService>>['summary'];

type SiloHeaderData = {
  totalRemainingStock: number;
  silobagQty: number;
  remainingStock: number;
  web_silobags_stock: number;
  filteredInputWeight: number;
  filteredExtractedWeight: number;
  filteredRemainingStock: number;
  totalFilteredSilobags: number;
};

type SilobagFilterProps = {
  updateHeaderData: (newState: SiloHeaderData) => void;
  handleGetSiloListFilter: (newState: SummarySilobagList | undefined) => void;
  handleGetParamsFilter: (newState: string | string[] | Record<string, string>) => void;
  handleGetSiloListAll: (newState: SummarySilobagList | undefined) => void;
  extractionSuccess?: boolean;
  silobagSelected?: (ids: string[]) => void;
  targetSilobagDataSelection: SummarySilobagList;
  onSilobagDataSelectionChange: (silobags?: SummarySilobagList) => void;
  disableFilterExportBtn?: (val: boolean) => void;
  setSiloListAll: (val: SummarySilobagList) => void;
  isSuccess: boolean;
  isSuccessClose: boolean;
  isMassiveSuccess: boolean;
  setIsNonValidatedSBList: (val: boolean) => void;
  setIsCloseList: (val: boolean) => void;
  isCloseList: boolean;
  IsSilobagValidated: boolean;
};

type Lot = Awaited<ReturnType<typeof listLotsService>>['summary'][0];

// const locales = { es: es };
// type LocaleKey = keyof typeof locales;
type LocaleKey = 'es';

export function SilobagListFilters({
  updateHeaderData,
  handleGetSiloListFilter,
  handleGetSiloListAll,
  handleGetParamsFilter,
  isSuccess,
  isSuccessClose,
  isMassiveSuccess,
  extractionSuccess,
  targetSilobagDataSelection,
  onSilobagDataSelectionChange,
  disableFilterExportBtn,
  setSiloListAll,
  setIsNonValidatedSBList,
  setIsCloseList,
  isCloseList,
  IsSilobagValidated,
}: SilobagFilterProps) {
  const {
    organizationId,
    userOrganizationId,
    filters,
    setFilters,
    filteredUrl,
    setFilteredUrl,
    preferedLanguageId,
  } = useGlobalStore(useShallow((s) => s));

  const deleteTagsFilter = () => setFilters({ ...filters, tag_ids: [] });

  const alertLevel: AlertLevelProp[] = [
    { id: 2, name: i18n.t('app.silobag_data.movement'), color: 'error' },
    { id: 4, name: i18n.t('app.silobag_data.quality_serious'), color: 'warning' },
    { id: 3, name: i18n.t('app.silobag_data.quality_min'), color: 'secondary' },
    { id: 1, name: i18n.t('app.silobag_data.optimun'), color: 'primary' },
  ];

  const siloSatSelected = [
    { id: 1, name: i18n.t('app.silobags_filters.only_actives'), value: true },
    { id: 2, name: i18n.t('app.silobags_filters.only_inactives'), value: false },
  ];

  const siloMetrixSelected = [
    { id: 1, name: i18n.t('app.silobags_filters.only_actives'), value: true },
    { id: 2, name: i18n.t('app.silobags_filters.only_inactives'), value: false },
  ];

  const corvusSelected = [
    { id: 1, name: i18n.t('app.silobags_filters.only_actives'), value: true },
    { id: 2, name: i18n.t('app.silobags_filters.only_inactives'), value: false },
  ];

  const DiscrepancyOptions = [
    { id: 1, name: i18n.t('app.with_inconsistencies'), value: true },
    { id: 2, name: i18n.t('app.without_inconsistencies'), value: false },
  ];

  const location = useLocation();
  const navigate = useNavigate();
  const [active, setActive] = useState<boolean>(filters.active);
  const [nonValidatedSb, setNonValidatedSb] = useState<number>(filters.silobag_data_status_id as number);
  const [saveSiloFirstResponse, setSaveSiloFirstResponse] = useState<boolean>(false);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | string>([null, null]);
  const [initRange, endRange] = dateRange;
  const initDate = initRange ? (initRange as Dayjs).format('YY-MM-DD') : null;
  const endDate = endRange ? (endRange as Dayjs).format('YY-MM-DD') : null;
  const [hasFilters, setHasFilters] = useState<boolean>(false);
  const locale: LocaleKey = 'es';
  const [selectedFilterNames, setSelectedFilterNames] = useState<Record<string, string>>({});
  const inputRef = useRef<HTMLInputElement>(null);
  const [countrysidesList, setCountrysidesList] = useState<SummaryCountrysidesList>([]);
  const [speciesList, setSpeciesList] = useState<SummarySpeciesList>([]);
  const [sortingModel, setSortingModel] = useState<string>('');
  const [expandAdvancedFilters, setExpandAdvancedFilters] = useToggle(false);
  const handleShowAdvanceFilters = () => {
    setExpandAdvancedFilters((prev: boolean) => !prev); // Alterna entre expandido/colapsado
  };

  const getUrlWithFilters = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('range');
    searchParams.delete('active');
    searchParams.delete('paginationModel');
    return `${searchParams ? `${searchParams.toString()}` : ''}`;
  };
  // Obtener la URL completa con los filtros aplicados y almacenarla en el estado
  useEffect(() => {
    const urlWithFilters = getUrlWithFilters();
    setFilteredUrl(urlWithFilters);
  }, [location.pathname, location.search, organizationId]);

  const updateURLWithFilters = () => {
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '' && key !== 'range' && key !== 'paginationModel') {
        queryParams.append(key, value as string);
      }
    });
    navigate(`?${queryParams.toString()}`);
  };

  function parseSortingModel(sortingModel: string) {
    const params: Record<string, string> = {};
    const byMatch = sortingModel.match(/orderBy=([^&]+)/);
    const dirMatch = sortingModel.match(/orderByDirections=([^&]+)/);
    if (byMatch) params.orderBy = byMatch[1];
    if (dirMatch) params.orderByDirections = dirMatch[1];
    return params;
  }

  useEffect(() => {
    updateURLWithFilters();
  }, [filters]);

  //Query Params silobag Service
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { paginationModel, countryside_zone_id, ...filtersWithoutPagination } = filters;
  const queryParamsSilobagService: queryParametersSbList = {
    ...filtersWithoutPagination,
    organization_id: organizationId,
    // active: active,
    // silobag_data_status_id: nonValidatedSb,
    take: paginationModel?.pageSize ?? 50,
    page: paginationModel?.page ?? 0,
    ...filtersWithoutPagination,
    ...parseSortingModel(sortingModel),
  };
  //Silobags Service
  const {
    trigger: getSiloData,
    summary: dataSilo,
    isLoading: isLoadingSiloData,
  } = useService(listSilobagService, {
    fetchOnMount: [{ query: queryParamsSilobagService }],
  });

  useDebounce(
    () => {
      getSiloData({ query: queryParamsSilobagService });
      if (filteredUrl.length) setHasFilters(true);
    },
    250,
    [organizationId, filteredUrl, active, filters, sortingModel, nonValidatedSb]
  );

  const handleTagsAssigned = () => {
    getSiloData({ query: queryParamsSilobagService });
  };

  const silobagList = dataSilo?.data;

  //Countrysides Service
  const queryCountrysidesList = { organization_id: organizationId };
  const {
    trigger: getCountrysides,
    summary: countrysides,
    isLoading: isLoadingCountryside,
  } = useService(listCountrysideService, { fetchOnMount: [{ query: queryCountrysidesList }] });

  //Lots Service
  const queryLotListService = { id: organizationId };
  const {
    trigger: getLots,
    summary: lots,
    isLoading: isLoadingLots,
  } = useService(listLotsService, { fetchOnMount: [{ query: queryLotListService }] });

  const countrysideZoneList = useMemo(() => {
    if (!countrysides) return [];

    // Flatten all zones
    const allZones =
      countrysides.flatMap((c) => ({ id: c.countryside_zone?.id, name: c.countryside_zone?.name })) ?? [];

    // Remove duplicates by name using a Map
    const uniqueZones = [...new Map(allZones.map((z) => [z.name, z])).values()];

    return uniqueZones;
  }, [countrysides]);

  // Species Service
  const { summary: species, isLoading: isLoadingSpecies } = useService(speciesListService, {
    fetchOnMount: true,
  });

  // Products Service
  const { summary: products, isLoading: isLoadingProducts } = useService(productListService, {
    fetchOnMount: true,
  });

  // Tags Service
  const { summary: tagsData } = useService(silobagTagsListService, {
    fetchOnMount: [{ query: { id: organizationId ?? 0 } }],
  });
  const allTagsByOrganization = tagsData?.response?.at(0)?.tags_organizations ?? [];
  const systemTags = allTagsByOrganization
    .filter((to) => to.tag.scope.value === 'system')
    .map((to) => to.tag);
  const orgTags = allTagsByOrganization
    .filter((to) => to.tag.scope.value === 'organization')
    .map((to) => to.tag);
  const allTags = [...systemTags, ...orgTags];

  // Harvests Service
  const { summary: harvests, isLoading: isLoadingHarvest } = useService(harvestListService, {
    fetchOnMount: true,
  });

  useEffectOnUpdate(() => {
    getCountrysides({ query: queryCountrysidesList });
    getLots({ query: queryLotListService });
  }, [organizationId]);

  useEffectOnUpdate(() => {
    setCountrysidesList(countrysides ?? []);
  }, [countrysides]);

  useEffectOnUpdate(() => {
    setSpeciesList(species ?? []);
  }, [species]);

  useEffect(() => {
    const saveFirstResponse = async () => {
      if (!saveSiloFirstResponse && dataSilo) {
        if (dataSilo.data.length > 0) {
          handleGetSiloListAll(dataSilo.data);
          //TODO: para que se usa ese handle aca ??
          //handleGetParamsFilter(dataSilo.data);
          setSaveSiloFirstResponse(true);
        }
      }
    };

    saveFirstResponse();
  }, [saveSiloFirstResponse, dataSilo]);

  useEffect(() => {
    disableFilterExportBtn?.(!hasFilters);
  }, [hasFilters]);

  //Massive Success
  useEffect(() => {
    if (isSuccess) {
      getSiloData({ query: queryParamsSilobagService });
      setHasFilters(true);
    }
  }, [isSuccess, organizationId, filteredUrl, active, sortingModel, filters]);

  //Extraction Success
  useEffectOnUpdate(() => {
    if (extractionSuccess) {
      getSiloData({ query: queryParamsSilobagService });
      setHasFilters(true);
    }
  }, [extractionSuccess]);

  // Update Silobags and remaining stock
  useEffectOnUpdate(() => {
    updateHeaderData({
      totalRemainingStock: dataSilo?.totalRemainingStock as number,
      silobagQty: dataSilo?.totalFilteredSilobags as number,
      remainingStock: dataSilo?.filteredRemainingStock as number,
      web_silobags_stock: dataSilo?.web_silobags_stock as number,
      totalFilteredSilobags: dataSilo?.totalFilteredSilobags as number,
      filteredInputWeight: dataSilo?.filteredInputWeight as number,
      filteredExtractedWeight: dataSilo?.filteredExtractedWeight as number,
      filteredRemainingStock: dataSilo?.filteredRemainingStock as number,
    });
    handleGetSiloListFilter(silobagList);
  }, [dataSilo]);

  // Handlers Select
  const updateNameFilter = (value: string) => {
    setFilters({ ...filters, name: value });
    setHasFilters(true);
    setSelectedFilterNames((prevNames) => ({ ...prevNames, name: value }));
    handleGetParamsFilter(Object.values(selectedFilterNames));
  };

  const handlerName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValue = e.target.value;
    clearTimeout(typingTimer);
    typingTimer = setTimeout(() => {
      updateNameFilter(latestSelectedValue);
    }, 500);
    latestSelectedValue = selectedValue;
  };
  let typingTimer: NodeJS.Timeout;
  let latestSelectedValue: string = '';

  const handlerSilobagProductTypeID = (e: SelectChangeEvent<unknown>) => {
    const selectedValue = e.target.value;
    setFilters({ ...filters, silobag_product_type_id: selectedValue as string });
    setHasFilters(true);

    const selectedProductName = products?.find(
      (item: { id: number | string }) => item.id === selectedValue
    )?.name;
    if (selectedProductName) {
      setSelectedFilterNames((prevNames) => ({ ...prevNames, product: selectedProductName }));
      handleGetParamsFilter(Object.values(selectedFilterNames));
    }
  };

  const handlerSilobagHarvestID = (e: SelectChangeEvent<unknown>) => {
    const selectedValue = e.target.value;
    setFilters({ ...filters, silobag_harvest_id: selectedValue as string });
    setHasFilters(true);

    const selectedHarvestName = harvests?.find(
      (item: { id: number | string }) => item.id === selectedValue
    )?.name;
    if (selectedHarvestName) {
      setSelectedFilterNames((prevNames) => ({ ...prevNames, harvest: selectedHarvestName }));
      handleGetParamsFilter(Object.values(selectedFilterNames));
    }
  };

  const handlerSilobagSpeciesTypeID = (e: SelectChangeEvent<unknown>) => {
    const selectedValue = e.target.value as number[];

    const selectedSpecies = speciesList.filter((item) => selectedValue.includes(item.id));

    const selectedSpeciesName = selectedSpecies.map((item) => item.name);
    setSelectedFilterNames((prevNames) => ({
      ...prevNames,
      specie: selectedSpeciesName.join(', ') ?? '',
    }));

    setFilters({ ...filters, silobag_species_type_id: selectedValue });
    setHasFilters(true);
  };

  const handlerCountrysideID = (e: SelectChangeEvent<unknown>) => {
    const selectedValue = e.target.value as number[];

    const selectedCountrysides = countrysidesList.filter((item) => selectedValue.includes(item.id));

    const selectedCountrysideName = selectedCountrysides.map((item) => item.name);
    setSelectedFilterNames((prevNames) => ({
      ...prevNames,
      countryside: selectedCountrysideName.join(', ') ?? '',
    }));

    setFilters({ ...filters, countryside_id: selectedValue });
    setHasFilters(true);
  };

  const handlerSiloSatValue = (e: SelectChangeEvent<unknown>) => {
    const selectedValues = e.target.value;

    const selectedSiloSat = siloSatSelected.find((item) => selectedValues === item.id);

    const selectedProductNames = selectedSiloSat?.name;

    setSelectedFilterNames((prevNames) => ({ ...prevNames, siloSatActive: selectedProductNames as string }));

    setFilters({ ...filters, sat_view_active: selectedSiloSat?.value as boolean });
  };

  const handlerSiloMetrixValue = (e: SelectChangeEvent<unknown>) => {
    const selectedValues = e.target.value;

    const selectedSiloMetrix = siloMetrixSelected.find((item) => selectedValues === item.id);

    const selectedProductNames = selectedSiloMetrix?.name;

    setSelectedFilterNames((prevNames) => ({
      ...prevNames,
      siloMetrixActive: selectedProductNames as string,
    }));

    setFilters({ ...filters, is_silo_metrix_active: selectedSiloMetrix?.value as boolean });
  };

  // TODO: REVISAR ESTE HANDLER CUANDO SE UTILICE CORVUS
  const handlerCorvusID = (e: SelectChangeEvent<unknown>) => {
    const selectedValues = e.target.value;

    const selectedCorvus = corvusSelected.find((item) => selectedValues === item.id);

    const selectedProductNames = selectedCorvus?.name;

    setSelectedFilterNames((prevNames) => ({
      ...prevNames,
      corvusActive: selectedProductNames as string,
    }));

    setFilters({ ...filters, corvus: selectedCorvus?.value as boolean });
  };

  const handleAlertLevel = (e: SelectChangeEvent<unknown>) => {
    const selectedValue = e.target.value;
    setFilters({ ...filters, color_id: selectedValue as string });
    setHasFilters(true);

    const selectedAlertLevelName = alertLevel.find(
      (item: { id: number | string }) => item.id === selectedValue
    )?.name;
    if (selectedAlertLevelName) {
      setSelectedFilterNames((prevNames) => ({ ...prevNames, alertLevel: selectedAlertLevelName }));
      handleGetParamsFilter(selectedAlertLevelName);
    }
  };

  const handleClickSiloClose = () => {
    setActive((prevActive) => !prevActive);
    setFilters({ ...filters, active: !active });
    setHasFilters(true);
    setIsCloseList(!isCloseList);
  };

  const handleClickNonValidatedSilobags = () => {
    const currentNonValidatedSb = filters.silobag_data_status_id;
    const newValue = currentNonValidatedSb === 2 ? 1 : 2;

    setNonValidatedSb(newValue);
    setFilters({ ...filters, silobag_data_status_id: newValue });
    setHasFilters(true);
  };

  useEffect(() => {
    if (filters.silobag_data_status_id === 1) {
      setIsNonValidatedSBList(true);
    } else {
      setIsNonValidatedSBList(false);
    }
  }, [filters]);

  useEffect(() => {
    if (isSuccessClose) {
      setFilters({ ...filters, active: false, silobag_data_status_id: IsSilobagValidated ? 1 : 2 });
      setActive(false);
      setHasFilters(true);
    }
  }, [isSuccessClose, IsSilobagValidated]);

  useEffectOnUpdate(() => {
    if (isMassiveSuccess) {
      setFilters({ ...filters, active: false });
      setActive(false);
      setHasFilters(true);
    }
  }, [isMassiveSuccess]);

  useEffect(() => {
    if (initDate !== null) {
      setFilters({ ...filters, from: initDate, range: dateRange as string });
      setSelectedFilterNames((prevNames) => ({ ...prevNames, initDate: `From: ${initDate}` }));
      setHasFilters(true);
    }
    if (endDate !== null) {
      setFilters({ ...filters, to: endDate, range: dateRange as string });
      setSelectedFilterNames((prevNames) => ({ ...prevNames, endDate: `To: ${endDate}` }));
      setHasFilters(true);
    }
  }, [initDate, endDate]);

  const handleLotsId = (e: SelectChangeEvent<unknown>) => {
    const selectedValue = e.target.value;
    setFilters({ ...filters, lot_id: selectedValue as string });
    // TODO: Rearmar esto -> filters.countryside_id ya no es string, es un Arreglo
    const targetCountrysideIDs = (filters.countryside_id.length > 0 ? lotByCountrySideId : lots) as Lot[];
    const selectedLotName = targetCountrysideIDs?.find((item) => item.id === selectedValue)?.name;
    if (selectedLotName) {
      setSelectedFilterNames((prevNames) => ({ ...prevNames, lot: selectedLotName }));
      handleGetParamsFilter(Object.values(selectedFilterNames));
      setHasFilters(true);
    }
  };

  const handleCountrysideZoneId = (e: SelectChangeEvent<unknown>) => {
    const selectedValue = e.target.value;

    const selectedCountrysideZoneName =
      countrysideZoneList?.find((item) => item.id === selectedValue)?.name ?? '';

    setFilters({
      ...filters,
      countryside_zone_id: selectedValue as string,
      countryside_zone_name: encodeURIComponent(selectedCountrysideZoneName),
    });
    setHasFilters(true);

    if (selectedCountrysideZoneName) {
      setSelectedFilterNames((prevNames) => ({ ...prevNames, countrysideZone: selectedCountrysideZoneName }));
      handleGetParamsFilter(Object.values(selectedFilterNames));
    }
  };

  useEffect(() => {
    handleGetParamsFilter(selectedFilterNames);
  }, [selectedFilterNames]);

  const removeFilter = () => {
    setFilters({
      countryside_id: [],
      lot_id: '',
      silobag_species_type_id: [],
      silobag_product_type_id: '',
      sat_view_active: '',
      is_silo_metrix_active: '',
      silobag_harvest_id: '',
      corvus: '',
      color_id: '',
      name: '',
      from: '',
      to: '',
      range: '',
      silobag_data_status_id: 2,
      active: true,
      inconsistencies: '',
      paginationModel: { page: 0, pageSize: 50 },
      countryside_zone_id: '',
      countryside_zone_name: '',
      tag_ids: [],
    });
    setNonValidatedSb(2);
    setActive(true);
    setDateRange([null, null]);
    setSelectedFilterNames({});
    setHasFilters(false);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    setIsCloseList(false);
  };

  useEffectOnUpdate(removeFilter, [organizationId]);

  const lotByCountrySideId =
    lots && lots.length > 0
      ? lots.filter((item) => filters.countryside_id.includes(item.countryside_id))
      : silobagList?.filter((item) => filters.countryside_id.includes(item.lots.countrysides.id));

  const hasNonEmptyFilters = (filters: Record<string, string>): boolean => {
    return Object.values(filters).some((value) => value !== '');
  };

  // TODO: Chequear si se puede simplificar estos delete en 2/3 funciones y usar el mismo delete para los filtros
  const deleteCountrysideFilter = () => {
    setSelectedFilterNames((prevNames) => ({
      ...prevNames,
      countryside: '',
    }));
    setFilters({ ...filters, countryside_id: [] });
  };

  const deleteSpeciesFilter = () => {
    setSelectedFilterNames((prevNames) => ({
      ...prevNames,
      specie: '',
    }));
    setFilters({ ...filters, silobag_species_type_id: [] });
  };

  const deleteAlertLevelFilter = () => {
    setFilters({ ...filters, color_id: '' });
    setSelectedFilterNames((prevNames) => ({ ...prevNames, alertLevel: '' }));
  };

  const deleteLotsFilter = () => {
    setFilters({ ...filters, lot_id: '' });
    setSelectedFilterNames((prevNames) => ({ ...prevNames, lot: '' }));
  };

  const deleteCountrysideZoneFilter = () => {
    setFilters({ ...filters, countryside_zone_id: '', countryside_zone_name: '' });
    setSelectedFilterNames((prevNames) => ({ ...prevNames, countrysideZone: '' }));
  };

  const deleteProductFilter = () => {
    setFilters({ ...filters, silobag_product_type_id: '' });
    setSelectedFilterNames((prevNames) => ({ ...prevNames, product: '' }));
  };

  const deleteHarvestFilter = () => {
    setFilters({ ...filters, silobag_harvest_id: '' });
    setSelectedFilterNames((prevNames) => ({ ...prevNames, harvest: '' }));
  };

  const deleteSiloSatFilter = () => {
    setSelectedFilterNames((prevNames) => ({ ...prevNames, siloSatActive: '' }));
    setFilters({ ...filters, sat_view_active: '' });
  };

  const deleteSiloMetrixFilter = () => {
    setSelectedFilterNames((prevNames) => ({ ...prevNames, siloMetrixActive: '' }));
    setFilters({ ...filters, is_silo_metrix_active: '' });
  };
  const deleteCorvusFilter = () => {
    setSelectedFilterNames((prevNames) => ({ ...prevNames, corvus: '' }));
    setFilters({ ...filters, corvus: '' });
  };

  const [offerSelectAllSilobagsButton, setOfferSelectAllSilobagsButton] = useState(false);
  const handleChangeOfferSelectAllSilobagsButton = (offer?: boolean | null) =>
    setOfferSelectAllSilobagsButton((prev) => offer ?? !prev);

  const {
    trigger: triggerSiloDataForSelection,
    // summary: silobagDataForSelection,
    isLoading: isSbDataForSelectionLoading,
  } = useService(listSilobagService, {
    onSuccess: (response) => {
      onSilobagDataSelectionChange(response.data);
    },
  });
  const handleSelectAllSilobags = () => {
    const { paginationModel, silobag_data_status_id, ...restFilters } = filters;
    silobag_data_status_id;
    paginationModel;

    triggerSiloDataForSelection({
      query: {
        ...restFilters,
        organization_id: organizationId,
      },
    });
  };

  const handleHaveDiscrepancy = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value;
    setFilters({ ...filters, inconsistencies: value == 1 ? true : false });
  };

  const deleteDiscrepancyFilter = () => {
    setFilters({ ...filters, inconsistencies: '' });
    setSelectedFilterNames((prevNames) => ({ ...prevNames, discrepancy: '' }));
  };

  const listSbLength = dataSilo?.totalFilteredSilobags ?? 0;
  const hasAllSilboagSelected = targetSilobagDataSelection.length >= listSbLength;

  return (
    <>
      {isLoadingCountryside &&
      isLoadingLots &&
      isLoadingSpecies &&
      isLoadingProducts &&
      isLoadingHarvest &&
      isLoadingSiloData ? (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <CircularProgress sx={{ marginLeft: '10px', color: 'green' }} size={24} />
        </div>
      ) : (
        <>
          <Grid
            container
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 100,
              backgroundColor: 'white',
              borderRadius: '8px',
              mb: 2,
              m: 0,
              mt: 2,
              p: 1,
              pb: expandAdvancedFilters ? 2 : 1,
            }}
            spacing={1}
          >
            <Grid item xs={12} md={2}>
              <FormControl sx={{ fontSize: 14, m: 0, p: 0 }}>
                <TextField
                  size="medium"
                  variant="outlined"
                  placeholder={i18n.t('app.silobag_list.search_by_name_id')}
                  onChange={handlerName}
                  inputRef={inputRef}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <img src={SearchIcon} alt="search-icon" />
                        </InputAdornment>
                      ),
                      inputProps: { style: { fontSize: 14 } },
                    },
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Selector
                label={i18n.t('app.silobag_data.specie')}
                id="silobag_species_type_id"
                value={filters?.silobag_species_type_id}
                onChange={(e) => handlerSilobagSpeciesTypeID(e)}
                multiple
                options={species}
                renderValue={() =>
                  species
                    ?.filter((specie) => filters?.silobag_species_type_id?.includes(specie.id))
                    .map((matchedSpecie) => matchedSpecie.name)
                    .join(', ')
                }
                selectedList={species?.filter((specie) =>
                  filters?.silobag_species_type_id?.includes(specie.id)
                )}
                deleteFilter={deleteSpeciesFilter}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Selector
                label={i18n.t('app.countrysides')}
                id="countryside_id"
                value={filters?.countryside_id?.map((item) => item)}
                onChange={(e) => handlerCountrysideID(e)}
                multiple
                options={countrysides}
                selectedList={countrysides?.filter((countryside) =>
                  filters?.countryside_id?.includes(countryside.id)
                )}
                renderValue={() =>
                  countrysides
                    ?.filter((countryside) => filters?.countryside_id?.includes(countryside.id))
                    .map((matchedCountryside) => matchedCountryside.name)
                    .join(', ')
                }
                deleteFilter={deleteCountrysideFilter}
                disabled={isLoadingCountryside}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Selector
                label={i18n.t('app.silobag_data.level_alert')}
                id="color_id"
                value={filters.color_id}
                onChange={(e) => handleAlertLevel(e)}
                options={alertLevel}
                deleteFilter={deleteAlertLevelFilter}
                renderValue={(selectedId) => {
                  const selected = alertLevel.find((item) => item.id === selectedId);
                  return (
                    selected && (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'start',
                          alignItems: 'center',
                        }}
                      >
                        <Chip
                          label=""
                          style={{
                            height: '0.7rem',
                            width: '1rem',
                            marginRight: '0.3rem',
                          }}
                          color={selected.color}
                        />
                        <span>{selected.name}</span>
                      </div>
                    )
                  );
                }}
                withChip
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Stack direction={'column'} spacing={-2}>
                <FormControlLabel
                  label={i18n.t('app.silobag_data.view_silo_close')}
                  control={<Checkbox {...label} color="success" checked={!filters.active} />}
                  labelPlacement="end"
                  onChange={handleClickSiloClose}
                />
                <FormControlLabel
                  label={i18n.t('app.silobag_data.view_non_validated_sb')}
                  control={
                    <Checkbox {...label} color="success" checked={filters.silobag_data_status_id === 1} />
                  }
                  labelPlacement="end"
                  onChange={handleClickNonValidatedSilobags}
                />
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    paddingTop: '8px',
                    gap: '8px',
                    paddingLeft: '12px',
                  }}
                >
                  <img src={viewIcon} alt="archive-icon" style={{ width: '20px', height: '20px' }} />
                  <Link
                    onClick={() => navigate(DYNAMIC_PATH.ARCHIVED)}
                    sx={{
                      textAlign: 'center',

                      cursor: 'pointer',
                      textDecoration: 'none',
                      color: '#22A7F0',
                      fontWeight: 500,
                    }}
                  >
                    {i18n.t('app.silobag_data.view_archived_silobags')}
                  </Link>
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md={2}>
              <Stack direction={'column'} alignItems={'start'}>
                <Button
                  sx={{
                    fontWeight: 600,
                    textTransform: 'none',
                    display:
                      (hasFilters && hasNonEmptyFilters(selectedFilterNames)) || filteredUrl.length > 0
                        ? 'flex'
                        : 'none',
                    color: '#FF0000',
                  }}
                  onClick={removeFilter}
                  startIcon={<img src={DeleteIcon} alt="delete-icon" />}
                >
                  {i18n.t('app.silobag_data.delete_filters')}
                </Button>
                <Button
                  sx={{ color: '#22A7F0', textTransform: 'none' }}
                  endIcon={
                    <img
                      src={ExpandMore}
                      alt="expand-more"
                      style={{
                        transform: expandAdvancedFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  }
                  variant="text"
                  onClick={handleShowAdvanceFilters}
                >
                  {i18n.t('app.silobag_data.advanced_filters')}
                </Button>
              </Stack>
            </Grid>
            <Stack
              direction={'row'}
              width={'100%'}
              sx={{
                paddingLeft: '5px',
                display: expandAdvancedFilters ? 'block' : 'none',
                maxHeight: expandAdvancedFilters ? '200px' : '0',
                transition: 'max-height 0.4s ease, transform 0.4s ease, opacity 0.4s ease',
                transform: expandAdvancedFilters ? 'translateY(0)' : 'translateY(-20px)',
                opacity: expandAdvancedFilters ? 1 : 0,
              }}
            >
              <Grid container style={{ marginTop: '5px', alignItems: 'center' }} spacing={{ xs: 1, md: 1 }}>
                <Grid item xs={12} md={2}>
                  <Selector
                    label={i18n.t('app.lots')}
                    id="lot_id"
                    value={filters.lot_id}
                    onChange={(e) => handleLotsId(e)}
                    options={filters.countryside_id.length > 0 ? lotByCountrySideId : lots}
                    selectedList={filters.lot_id}
                    deleteFilter={deleteLotsFilter}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Selector
                    label={i18n.t('app.administrator.countryside_zone')}
                    id="countryside_zone_id"
                    value={filters.countryside_zone_id}
                    onChange={(e) => handleCountrysideZoneId(e)}
                    options={countrysideZoneList}
                    selectedList={filters.countryside_zone_id}
                    deleteFilter={deleteCountrysideZoneFilter}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Selector
                    label={i18n.t('app.silobag_data.product')}
                    id="silobag_product_type_id"
                    value={filters.silobag_product_type_id}
                    onChange={(e) => handlerSilobagProductTypeID(e)}
                    options={products}
                    selectedList={filters.silobag_product_type_id}
                    deleteFilter={deleteProductFilter}
                  />
                </Grid>
                <Grid item xs={12} md={1.95}>
                  <FormControl variant="outlined" size="medium" sx={{ ...stylesInput }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
                      <SingleInputDateRangePicker
                        label={i18n.t('app.silobag_data.range_storage')}
                        name={i18n.t('app.silobag_data.range_storage')}
                        onChange={(newValue) => setDateRange(newValue)}
                        value={filters.range !== '' ? filters.range : dateRange}
                      />
                    </LocalizationProvider>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Selector
                    label={i18n.t('app.silobag_data.harvest')}
                    id="silobag_harvest_id"
                    value={filters.silobag_harvest_id}
                    onChange={(e) => handlerSilobagHarvestID(e)}
                    options={harvests}
                    selectedList={filters.silobag_harvest_id}
                    deleteFilter={deleteHarvestFilter}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Selector
                    label="Corvus"
                    id="corvus"
                    value={filters.corvus}
                    onChange={handlerCorvusID}
                    options={corvusSelected}
                    selectedList={filters.corvus}
                    deleteFilter={deleteCorvusFilter}
                    renderValue={() => corvusSelected.find((item) => item.value === filters.corvus)?.name}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Selector
                    label={i18n.t('app.silobag_data.inconsistency')}
                    id="inconsistencia"
                    value={filters.inconsistencies}
                    onChange={handleHaveDiscrepancy}
                    options={DiscrepancyOptions}
                    selectedList={filters.inconsistencies}
                    deleteFilter={deleteDiscrepancyFilter}
                    renderValue={() =>
                      DiscrepancyOptions.find((item) => item.value === filters.inconsistencies)?.name
                    }
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Selector
                    label="SiloSat"
                    id="silo_sat"
                    value={filters.sat_view_active}
                    onChange={(e) => handlerSiloSatValue(e)}
                    options={siloSatSelected}
                    selectedList={filters.sat_view_active}
                    deleteFilter={deleteSiloSatFilter}
                    renderValue={() =>
                      siloSatSelected.find((item) => item.value === filters.sat_view_active)?.name
                    }
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Selector
                    label="SiloMetrix"
                    id="silo_metrix"
                    value={filters.is_silo_metrix_active}
                    onChange={(e) => handlerSiloMetrixValue(e)}
                    options={siloMetrixSelected}
                    selectedList={filters.is_silo_metrix_active}
                    deleteFilter={deleteSiloMetrixFilter}
                    renderValue={() =>
                      siloMetrixSelected.find((item) => item.value === filters.is_silo_metrix_active)?.name
                    }
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Selector
                    disabled
                    label={i18n.t('app.silobag_data.silo_with_movement')}
                    id="sb_with_mov"
                    value=""
                    onChange={handlerCorvusID}
                    options={harvests}
                    selectedList={[]}
                    deleteFilter={deleteCorvusFilter}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={2}
                  sx={{ display: organizationId === userOrganizationId ? '' : 'none' }}
                >
                  <Tooltip
                    title={orgTags.length === 0 ? i18n.t('app.silobag_tags.support') : ''}
                    placement="top"
                  >
                    <span>
                      <FormControl variant="outlined" size="medium" sx={{ ...stylesInput, width: '98%' }}>
                        <InputLabel id="tag-filter-label">{i18n.t('app.silobag_tags.tags')}</InputLabel>
                        <Select
                          labelId="tag-filter-label"
                          multiple
                          disabled={orgTags.length === 0}
                          value={filters.tag_ids.map(String)}
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              tag_ids: (e.target.value as string[]).map(Number),
                            })
                          }
                          input={<OutlinedInput label="Etiquetas" />}
                          renderValue={(selected) =>
                            (selected as string[])
                              .map((id) => allTags.find((t) => String(t.id) === id)?.label ?? id)
                              .join(', ')
                          }
                          endAdornment={
                            <IconButton
                              onClick={deleteTagsFilter}
                              sx={{ visibility: filters.tag_ids.length > 0 ? 'visible' : 'hidden', mr: 1 }}
                              size="small"
                            >
                              <img src={CloseIcon} alt="clear" style={{ width: 14, height: 14 }} />
                            </IconButton>
                          }
                          sx={{
                            '& .MuiSelect-outlined ~ .MuiSelect-icon': {
                              display: filters.tag_ids.length > 0 ? 'none' : '',
                            },
                          }}
                        >
                          {systemTags.length > 0 && (
                            <ListSubheader
                              sx={{ fontWeight: 700, fontSize: 12, lineHeight: '28px', color: '#374151' }}
                            >
                              Etiquetas del sistema
                            </ListSubheader>
                          )}
                          {systemTags.length > 0 &&
                            systemTags.map((tag) => {
                              const translation = tag.translations.find(
                                (p) => p.language == preferedLanguageId
                              )?.value;
                              const translationTooltip = tag.translations.find(
                                (p) => p.language == preferedLanguageId
                              )?.tooltip;

                              return (
                                <MenuItem key={tag.id} value={String(tag.id)}>
                                  <Checkbox
                                    checked={filters.tag_ids.includes(tag.id)}
                                    size="small"
                                    sx={{ p: 0, mr: 1, '&.Mui-checked': { color: '#6BAA00' } }}
                                  />
                                  <Tooltip title={translationTooltip}>
                                    <Chip
                                      icon={
                                        tag.image_url ? (
                                          <img
                                            src={tag.image_url}
                                            alt=""
                                            style={{
                                              width: 14,
                                              height: 14,
                                              marginLeft: 4,
                                              objectFit: 'contain',
                                            }}
                                          />
                                        ) : undefined
                                      }
                                      label={translation}
                                      size="small"
                                      sx={{
                                        bgcolor: tag.bg_color,
                                        color: tag.text_color,
                                        fontWeight: 600,
                                        fontSize: 11,
                                        height: 22,
                                        borderRadius: '5px',
                                        pointerEvents: 'none',
                                        '& .MuiChip-label': { px: '8px' },
                                      }}
                                    />
                                  </Tooltip>
                                </MenuItem>
                              );
                            })}
                          <ListSubheader
                            sx={{ fontWeight: 700, fontSize: 12, lineHeight: '28px', color: '#374151' }}
                          >
                            {i18n.t('app.silobag_tags.customized')}
                          </ListSubheader>
                          {orgTags.map((tag) => {
                            const translation = tag.translations.find(
                              (p) => p.language == preferedLanguageId
                            )?.value;
                            return (
                              <MenuItem key={tag.id} value={String(tag.id)}>
                                <Checkbox
                                  checked={filters.tag_ids.includes(tag.id)}
                                  size="small"
                                  sx={{ p: 0, mr: 1, '&.Mui-checked': { color: '#6BAA00' } }}
                                />
                                <Chip
                                  icon={
                                    tag.image_url ? (
                                      <img
                                        src={tag.image_url}
                                        alt=""
                                        style={{ width: 14, height: 14, marginLeft: 4, objectFit: 'contain' }}
                                      />
                                    ) : undefined
                                  }
                                  label={translation}
                                  size="small"
                                  sx={{
                                    bgcolor: tag.bg_color,
                                    color: tag.text_color,
                                    fontWeight: 600,
                                    fontSize: 11,
                                    height: 22,
                                    borderRadius: '5px',
                                    pointerEvents: 'none',
                                    '& .MuiChip-label': { px: '8px' },
                                  }}
                                />
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </span>
                  </Tooltip>
                </Grid>
              </Grid>
            </Stack>
          </Grid>
          {offerSelectAllSilobagsButton ? (
            <Paper elevation={0} sx={{ width: '100%', marginTop: '20px' }}>
              <Stack direction="row" gap={2} justifyContent="center" alignItems="center" padding={2}>
                <Typography fontSize={14} color="textSecondary" style={{ marginRight: 16 }}>
                  {i18n.t('app.silobag_list.total_selection.total_info')} {targetSilobagDataSelection?.length}{' '}
                  {i18n.t('app.silobag_list.total_selection.total_silobags_info')}
                </Typography>
                <Button
                  disabled={isSbDataForSelectionLoading}
                  sx={{
                    color: '#22A7F0',
                    fontSize: '0.75rem',
                    padding: '0 3px',
                    textTransform: 'none',
                    '&:hover': { background: '#22A7F02A' },
                  }}
                  onClick={handleSelectAllSilobags}
                >
                  <Stack direction="row" gap={1} justifyContent="center" alignItems="center">
                    <span>{i18n.t('app.silobag_list.total_selection.total_silobags_selection')}</span>
                  </Stack>
                </Button>
                {hasAllSilboagSelected ? (
                  <Button
                    disabled={isSbDataForSelectionLoading}
                    sx={{
                      color: '#E53935',
                      fontSize: '0.75rem',
                      padding: '0 3px',
                      textTransform: 'none',

                      '&:hover': { background: '#E539352A' },
                    }}
                    onClick={() => {
                      onSilobagDataSelectionChange();
                      setOfferSelectAllSilobagsButton(false);
                    }}
                  >
                    <Stack direction="row" gap={1} justifyContent="center" alignItems="center">
                      <span>{i18n.t('app.silobag_list.total_selection.deselection')}</span> (
                      {targetSilobagDataSelection?.length})
                    </Stack>
                  </Button>
                ) : null}
                {isSbDataForSelectionLoading ? (
                  <CircularProgress sx={{ marginLeft: '10px', color: '#22A7F0' }} size={16} thickness={8} />
                ) : null}
              </Stack>
            </Paper>
          ) : null}
          <SilobagListTablet
            silobagList={silobagList}
            isLoading={isLoadingSiloData || isSbDataForSelectionLoading}
            isActiveList={active}
            rowCount={dataSilo?.totalFilteredSilobags ?? 0}
            paginationModel={filters.paginationModel}
            setPaginationModel={(fn) => {
              const paginationModel = fn(filters.paginationModel);
              setFilters({ ...filters, paginationModel });
            }}
            // sortingModel={sortingModel}
            setSortingModel={setSortingModel}
            targetSilobagDataSelection={targetSilobagDataSelection}
            onSilobagDataSelectionChange={onSilobagDataSelectionChange}
            setSiloListAll={setSiloListAll}
            onOfferSelectAllSilobagsChange={handleChangeOfferSelectAllSilobagsButton}
            onTagsAssigned={handleTagsAssigned}
          />
        </>
      )}
    </>
  );
}
